import { Component, NgZone, OnInit } from '@angular/core';
import {
  Option,
  OptionStatus,
  Price,
  Product,
} from '@dch/products-business-logic';
import {
  buildWebshopOrderInfo,
  FI_NSP_OPTION,
  getNspPrice,
  INspPrices,
  INspValue,
  INspValues,
  MV_NSP_OPTION,
  NSP_TRY_AND_BUY_OPTIONS,
} from '../utils/nsp.utils';
import { Store } from '@ngrx/store';
import { OnDestroyHookDirective } from '../../shared/hooks';
import {
  getNspInternetValues,
  getNspMobileValues,
  getNspPrices,
} from '../../store/reducers';
import { combineLatest } from 'rxjs';
import { WebshopUtils } from '../../shared/webshop/webshop-utils';
import {
  LevelType,
  MessageType,
  PxModalMessageService,
} from '@proximus/ng-common-components';
import {
  AnalyticsEventData,
  AnalyticsService,
  TranslationService,
} from '@proximus/ng-common';
import { Router } from '@angular/router';
import {AlwaysProtectedCheckService} from "../../services/always-protected-check.service";

@Component({
  selector: 'mprd-nsp-basket-modal',
  templateUrl: './nsp-basket-modal.component.html',
})
export class NspBasketModalComponent
  extends OnDestroyHookDirective
  implements OnInit
{
  internetNspValuesUnfiltered: INspValues;
  internetNspValues: INspValues;
  mobileNspValuesUnfiltered: INspValues;
  mobileNspValues: INspValues;
  isStandalone = false;
  hasInternetProduct = false;
  hasMobileProduct = false;
  isActivationFlow = false;
  hasTrialOption = false;
  fakeTotalOption: Option;
  nspPrices: INspPrices;
  nspInternetFakeOptionPrice: Option;
  nspMobileFakeOptionPrice: Option;
  nspAddedList: INspValue[] = [];
  nspRemovedList: INspValue[] = [];
  isOrdering = false;
  products: Product[];
  jsonProduct: any;
isAlwaysProtected = false;
  constructor(
    private store: Store,
    private ngZone: NgZone,
    private pxModalMessageService: PxModalMessageService,
    private translationService: TranslationService,
    private router: Router,
    private analyticsService: AnalyticsService,
    private alwaysProtectedCheckService: AlwaysProtectedCheckService
  ) {
    super();
  }

  ngOnInit(): void {
    this.subscribeToWebshopEvents();
    this.addSubscription(
      combineLatest([
        this.store.select(getNspInternetValues),
        this.store.select(getNspMobileValues),
        this.store.select(getNspPrices),
      ]).subscribe((result: any) => {
        const internetValues = result[0];
        const mobileValues = result[1];
        const nspPrices = result[2];
        if (internetValues) {
          this.internetNspValuesUnfiltered = internetValues;
          this.internetNspValues = this.filterDictionary(
            internetValues,
            (value) => value.value,
          );
          this.hasInternetProduct =
            this.getNspKeys(this.internetNspValues).length > 0;
        }
        if (mobileValues) {
          this.mobileNspValuesUnfiltered = mobileValues;
          this.mobileNspValues = this.filterDictionary(
            mobileValues,
            (value) => value.value,
          );
          this.hasMobileProduct =
            this.getNspKeys(this.mobileNspValues).length > 0;
        }
        this.nspPrices = nspPrices;
        this.nspInternetFakeOptionPrice = this.buildFakeOption(
          getNspPrice(this.nspPrices, FI_NSP_OPTION, 0),
          false,
        );
        this.nspMobileFakeOptionPrice = this.buildFakeOption(
          getNspPrice(this.nspPrices, MV_NSP_OPTION, 0),
          false,
        );

        this.products = this.getNSPProducts(
          this.internetNspValues,
          this.mobileNspValues,
        );
        this.jsonProduct = this.buildTaggingProduct(this.products);
        this.computeTotal();
        this.computeChanges();
      }),
    );

    this.alwaysProtectedCheckService.currentAlwaysProtected.subscribe(data => {
      this.isAlwaysProtected = data
    })
  }

  public getNspKeys(nspValues: INspValues) {
    return Object.keys(nspValues);
  }

  public confirm() {
    this.isOrdering = true;
    WebshopUtils.manageNspProducts(
      buildWebshopOrderInfo(
        this.internetNspValuesUnfiltered,
        this.mobileNspValuesUnfiltered,
      ),
    );
  }

  private getNSPProducts(internetNsp, mobileNsp) {
    const products = internetNsp
      ? mobileNsp
        ? [].concat(internetNsp, mobileNsp)
        : [].concat(internetNsp)
      : mobileNsp
        ? [].concat(mobileNsp)
        : [];
    return products;
  }

  private computeTotal() {
    let totalVat = 0;
    let totalNoVat = 0;
    let totalCalculatedVat = 0;
    let totalCalculatedNoVat = 0;

    if (this.internetNspValues) {
      this.getNspKeys(this.internetNspValues).forEach((key, index) => {
        const value = this.internetNspValues[key];
        const price = getNspPrice(this.nspPrices, value.mpcUuid, index);
        if (NSP_TRY_AND_BUY_OPTIONS.includes(value.mpcUuid)) {
          this.hasTrialOption = true;
        }
        totalVat += price.priceVat || 0;
        totalNoVat += price.priceNoVat || 0;
        totalCalculatedVat += price.calculatedPriceVat || 0;
        totalCalculatedNoVat += price.calculatedPriceNoVat || 0;
      });
    }
    if (this.mobileNspValues) {
      this.getNspKeys(this.mobileNspValues).forEach((key, index) => {
        const value = this.mobileNspValues[key];
        const price = getNspPrice(this.nspPrices, value.mpcUuid, index);
        if (NSP_TRY_AND_BUY_OPTIONS.includes(value.mpcUuid)) {
          this.hasTrialOption = true;
        }
        totalVat += price.priceVat || 0;
        totalNoVat += price.priceNoVat || 0;
        totalCalculatedVat += price.calculatedPriceVat || 0;
        totalCalculatedNoVat += price.calculatedPriceNoVat || 0;
      });
    }
    this.fakeTotalOption = this.buildFakeOption(
      {
        priceVat: totalVat,
        priceNoVat: totalNoVat,
        calculatedPriceVat: this.hasTrialOption
          ? totalCalculatedVat
          : undefined,
        calculatedPriceNoVat: this.hasTrialOption
          ? totalCalculatedNoVat
          : undefined,
        chargeFrequency: 'MON',
      },
      true,
    );
  }

  private buildFakeOption(price: any, includeFrequency: boolean) {
    return {
      ibStatus: OptionStatus.AVAILABLE,
      hideIncludedLabel: true,
      price: {
        calculatedPriceNoVat: price.calculatedPriceNoVat,
        calculatedPriceVat: price.calculatedPriceVat,
        priceNoVat: price.priceNoVat,
        priceVat: price.priceVat,
        chargeFrequency: includeFrequency ? price.chargeFrequency : undefined,
      } as Price,
    } as Option;
  }

  private filterDictionary(
    inputNspValues: INspValues,
    filterFunction: (value: INspValue, key: string) => boolean,
  ): INspValues {
    const filteredNspValues: INspValues = {};
    for (const key of Object.keys(inputNspValues)) {
      const value = inputNspValues[key];
      if (filterFunction(value, key)) filteredNspValues[key] = value;
    }
    return filteredNspValues;
  }

  private computeChanges() {
    this.nspAddedList = [];
    this.nspRemovedList = [];
    if (!this.isActivationFlow && this.mobileNspValuesUnfiltered) {
      this.getNspKeys(this.mobileNspValuesUnfiltered).forEach((key) => {
        const value = this.mobileNspValuesUnfiltered[key];
        if (value && value.value && !value.active) {
          this.nspAddedList.push(value);
        }
        if (value && !value.value && value.active) {
          this.nspRemovedList.push(value);
        }
      });
    }
  }

  private subscribeToWebshopEvents() {
    WebshopUtils.subscribeToConfirmationEvent((data: any) => {
      this.ngZone.run(() => {
        if (data.productDetails && data.action) {
          this.pxModalMessageService.dispatchMessage(
            MessageType.Confirm,
            LevelType.Root,
            this.translationService.getTranslatedValueFor(
              'nsp.basket.order.success.message',
            ),
            this.translationService.getTranslatedValueFor(
              'nsp.basket.order.success.title',
            ),
            false,
            true,
            'nspOrderSuccess',
            false,
            () => this.onModalMessageClose(),
          );
          this.sendTaggingEvent({
            event: 'popup-impression',
            popup: { zone: 'nsp', name: 'order success' },
            action: { name: 'order' },
            product: this.buildTaggingProduct(this.products),
          });
        }
      });
    }, this.addSubscription);

    WebshopUtils.subscribeToErrorEvent((data: any) => {
      this.ngZone.run(() => {
        if (
          data.products &&
          data.action &&
          data.httpCode &&
          data.httpCode === 402 &&
          data.message &&
          data.message === 'shoppingcart.ccw.validation.failed.customer.ibl'
        ) {
          this.pxModalMessageService.dispatchMessage(
            MessageType.Error,
            LevelType.Root,
            this.translationService.getTranslatedValueFor(
              'message.error.description.cwc',
            ),
            this.translationService.getTranslatedValueFor(
              'message.error.title',
            ),
            false,
            true,
            'nspOrderErrorCwc',
          );
          this.sendTaggingEvent({
            event: 'popup-impression',
            popup: { zone: 'nsp', name: 'order error cwc' },
            action: { name: 'order' },
            product: this.buildTaggingProduct(this.products),
          });
        } else {
          this.pxModalMessageService.dispatchMessage(
            MessageType.Error,
            LevelType.Root,
            this.translationService.getTranslatedValueFor(
              'nsp.basket.order.error.message',
            ),
            this.translationService.getTranslatedValueFor(
              'message.error.title',
            ),
            false,
            true,
            'nspOrderError',
          );
          this.sendTaggingEvent({
            event: 'popup-impression',
            popup: { zone: 'nsp', name: 'order error' },
            action: { name: 'order' },
            product: this.buildTaggingProduct(this.products),
          });
        }
        this.isOrdering = false;
      });
    }, this.addSubscription);
  }

  private onModalMessageClose() {
    if (this.isOrdering) {
      this.isOrdering = false;
      this.router.navigate(['/overview']);
    }
  }

  buildTaggingProduct(products: Product[]) {
    return JSON.stringify({
      id: 'standAlone',
      products: products,
    });
  }

  private sendTaggingEvent(data: AnalyticsEventData) {
    this.analyticsService.reportEvent(data);
  }
}
